import { useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { BookOpen, Building2, ChefHat, Calculator, LayoutGrid, ChevronsUpDown, CreditCard, LayoutDashboard, LogOut, Menu as MenuIcon, Plus, Receipt, Settings, Shield, Store, UserRound, Users, X } from "lucide-react";
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Progress, cn } from "@dishfolio/ui";
import { authClient, signOut } from "../lib/auth.ts";
import { useOrg } from "../lib/org.tsx";
import { useRestaurants } from "../lib/queries.ts";
import { Logo } from "./Logo.tsx";
import { NewLocationButton } from "./NewLocation.tsx";

function NavLink({ to, icon, children, exact, onClick, params }: { to: string; icon: React.ReactNode; children: React.ReactNode; exact?: boolean; onClick?: () => void; params?: Record<string, string> }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const href = params ? Object.entries(params).reduce((acc, [k, v]) => acc.replace(`$${k}`, v), to) : to;
  const active = exact ? path === href : path === href || path.startsWith(`${href}/`);
  return (
    <Link
      to={to as never}
      params={params as never}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground [&_svg]:size-4",
        active && "bg-card text-foreground shadow-xs ring-1 ring-border",
      )}
    >
      {icon}
      {children}
    </Link>
  );
}

function OrgSwitcher() {
  const { me, orgId, setOrgId, org } = useOrg();
  const navigate = useNavigate();
  const current = me.organizations.find((o) => o.id === orgId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-lg border bg-card px-2.5 py-2 text-left shadow-xs hover:bg-accent">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">{current?.name.slice(0, 2).toUpperCase()}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{current?.name}</span>
          <span className="block text-xs text-muted-foreground capitalize">
            {org?.plan.name ?? "…"} · {current?.role}
          </span>
        </span>
        <ChevronsUpDown className="size-4 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="start">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        {me.organizations.map((o) => (
          <DropdownMenuItem key={o.id} onSelect={() => (setOrgId(o.id), navigate({ to: "/" }))}>
            <Building2 /> <span className="flex-1 truncate">{o.name}</span>
            {o.id === orgId ? <Badge variant="secondary">Current</Badge> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate({ to: "/onboarding" })}>
          <Plus /> New organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreditsMeter() {
  const { org } = useOrg();
  const usage = org?.subscription.usage;
  if (!usage || usage.creditsGranted === 0) return null;
  return (
    <div className="rounded-lg border bg-card p-3 text-xs">
      <div className="mb-1.5 flex justify-between">
        <span className="font-medium">AI credits</span>
        <span className="text-muted-foreground">
          {usage.creditsRemaining} / {usage.creditsGranted}
        </span>
      </div>
      <Progress value={(usage.creditsUsed / usage.creditsGranted) * 100} className="h-1.5" />
    </div>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can, feature, org } = useOrg();
  const restaurants = useRestaurants();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col gap-4 p-3">
      <div className="px-1.5 pt-1">
        <Logo />
      </div>
      <OrgSwitcher />
      <nav className="flex flex-col gap-0.5" aria-label="Main">
        <NavLink to="/" exact icon={<LayoutDashboard />} onClick={onNavigate}>
          Overview
        </NavLink>
        {can("menu:edit") || can("analytics:view") ? (
          <NavLink to="/menus" icon={<BookOpen />} onClick={onNavigate}>
            Menus
          </NavLink>
        ) : null}
        {can("orders:view") && org?.plan.tableOrdering ? (
          <NavLink to="/orders" icon={<Receipt />} onClick={onNavigate}>
            Orders
          </NavLink>
        ) : null}
        {can("orders:view") && org?.plan.tableOrdering ? (
          <NavLink to="/kitchen" icon={<ChefHat />} onClick={onNavigate}>
            Kitchen display
          </NavLink>
        ) : null}
        {can("orders:manage") && org?.plan.tableOrdering && feature("pos") ? (
          <NavLink to="/pos" icon={<Calculator />} onClick={onNavigate}>
            POS
          </NavLink>
        ) : null}
        {can("orders:view") && org?.plan.tableOrdering && feature("table_management") ? (
          <NavLink to="/floor" icon={<LayoutGrid />} onClick={onNavigate}>
            Floor
          </NavLink>
        ) : null}
      </nav>
      <div className="flex flex-col gap-0.5">
        <div className="px-2.5 pb-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Locations</div>
        {restaurants.data?.map((r) => (
          <NavLink key={r.id} to="/restaurants/$restaurantId" params={{ restaurantId: r.id }} icon={<Store />} onClick={onNavigate}>
            <span className="truncate">{r.name}</span>
          </NavLink>
        ))}
        <NewLocationButton onNavigate={onNavigate} />
      </div>
      <nav className="flex flex-col gap-0.5" aria-label="Organization">
        <div className="px-2.5 pb-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Organization</div>
        <NavLink to="/team" icon={<Users />} onClick={onNavigate}>
          Team
        </NavLink>
        {can("billing:manage") ? (
          <NavLink to="/billing" icon={<CreditCard />} onClick={onNavigate}>
            Plan & billing
          </NavLink>
        ) : null}
        {can("org:manage") ? (
          <NavLink to="/settings" icon={<Settings />} onClick={onNavigate}>
            Settings
          </NavLink>
        ) : null}
      </nav>
      <div className="mt-auto flex flex-col gap-3">
        {me.user.role === "admin" && !me.impersonator ? (
          <NavLink to="/admin" icon={<Shield />} onClick={onNavigate}>
            Admin
          </NavLink>
        ) : null}
        <CreditsMeter />
        <DropdownMenu>
          <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent">
            <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-xs font-semibold">{me.user.name.slice(0, 1).toUpperCase()}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{me.user.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{me.user.email}</span>
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem
              onSelect={async () => {
                await signOut();
                localStorage.removeItem("dishfolio.org");
                await navigate({ to: "/login" });
                window.location.reload();
              }}
            >
              <LogOut /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/** Shown while a platform admin is signed in as another user. */
export function ImpersonationBanner() {
  const { me } = useOrg();
  const [busy, setBusy] = useState(false);
  if (!me.impersonator) return null;
  const stop = async () => {
    setBusy(true);
    await authClient.admin.stopImpersonating();
    try {
      localStorage.removeItem("dishfolio.org");
    } catch {
      /* ignore */
    }
    window.location.href = "/admin/users";
  };
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-warning px-4 py-2 text-center text-sm font-medium text-foreground">
      <UserRound className="size-4" />
      <span>
        You’re signed in as <b>{me.user.name}</b> ({me.user.email}). Changes you make are real.
      </span>
      <Button size="sm" variant="outline" onClick={stop} loading={busy}>
        Stop impersonating
      </Button>
    </div>
  );
}

export function AppShell() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex h-full flex-col">
      <ImpersonationBanner />
      <div className="flex min-h-0 flex-1">
      <aside className="hidden w-64 shrink-0 border-r bg-sidebar lg:block">
        <Sidebar />
      </aside>
      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-sidebar shadow-xl">
            <Button variant="ghost" size="icon-sm" className="absolute top-3 right-3" onClick={() => setOpen(false)} aria-label="Close menu">
              <X />
            </Button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-2 border-b px-4 lg:hidden">
          <Button variant="ghost" size="icon-sm" onClick={() => setOpen(true)} aria-label="Open menu">
            <MenuIcon />
          </Button>
          <Logo />
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}
